import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { ExpenseHeads } from "@/components/dashboard/ServiceProfitabilityCard";

const istDay = (iso, back = 0) => (iso ? new Date(new Date(iso).getTime() - back).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "");
import { PackageMinus, Receipt, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, Info } from "lucide-react";

const PERIODS = [
  { k: "today", label: "Today" },
  { k: "7d", label: "7 Days" },
  { k: "30d", label: "30 Days" },
  { k: "this_month", label: "This Month" },
  { k: "last_month", label: "Last Month" },
];

function Delta({ pct, invert }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-slate-400">—</span>;
  const up = pct > 0;
  // for costs, up is attention (amber), down is good (emerald); invert=false default (cost semantics)
  const good = invert ? up : !up;
  const Icon = pct === 0 ? Minus : up ? TrendingUp : TrendingDown;
  const cls = pct === 0 ? "text-slate-400" : good ? "text-emerald-600" : "text-amber-600";
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${cls}`}><Icon className="h-3 w-3" />{Math.abs(pct)}%</span>;
}

const TONE = {
  warn: { c: "border-amber-200 bg-amber-50 text-amber-800", I: AlertTriangle },
  good: { c: "border-emerald-200 bg-emerald-50 text-emerald-800", I: CheckCircle2 },
  info: { c: "border-slate-200 bg-slate-50 text-slate-700", I: Info },
};

export function MoneyIntel() {
  const [period, setPeriod] = useState("30d");
  const { data, isLoading } = useQuery({
    queryKey: ["money-intel", period],
    queryFn: async () => (await api.get(`/dashboard/money-intel?period=${period}`)).data,
    refetchOnWindowFocus: false,
  });
  const ic = data?.internal_consumption;
  const ex = data?.expenses;

  return (
    <div className="rounded-3xl border border-pawkt-border bg-white p-5 sm:p-6 shadow-card" data-testid="money-intel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-extrabold text-pawkt-text">Money Intelligence</h3>
          <p className="text-xs text-pawkt-text-secondary">Where money is going · {data?.period?.label || "…"} vs {data?.period?.comparison || "previous"}</p>
        </div>
        <div className="inline-flex rounded-full bg-pawkt-elevated p-1" data-testid="money-intel-period" role="tablist">
          {PERIODS.map((p) => (
            <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`mi-period-${p.k}`} role="tab" aria-selected={period === p.k}
              className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${period === p.k ? "bg-white text-pawkt-primary-deep shadow-card" : "text-slate-500 hover:text-slate-800"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-slate-400">Loading…</p>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Internal Consumption */}
          <div className="rounded-2xl border border-slate-100 p-4" data-testid="mi-consumption">
            <div className="flex items-center gap-2 text-slate-700"><PackageMinus className="h-4 w-4" /><span className="text-sm font-bold">Internal Consumption</span></div>
            {(!ic || ic.total_cost === 0) ? (
              <p className="mt-3 text-sm text-slate-400" data-testid="mi-consumption-empty">No internal consumption recorded.</p>
            ) : (
              <>
                <div className="mt-2 flex items-end gap-2">
                  <span className="font-display text-2xl font-extrabold text-pawkt-text">{inr(ic.total_cost)}</span>
                  {ic.has_comparison ? <Delta pct={ic.change_pct} /> : <span className="text-xs text-slate-400">No comparison data yet</span>}
                </div>
                <p className="text-[11px] text-slate-400">{ic.events} consumption event{ic.events === 1 ? "" : "s"}</p>
                <div className="mt-3 space-y-1.5">
                  {ic.categories.slice(0, 5).map((c) => (
                    <div key={c.category} className="flex items-center justify-between text-xs" data-testid={`mi-cons-cat-${c.category}`}>
                      <span className="text-slate-600 truncate">{c.category}{c.pct_of_total !== null ? ` · ${c.pct_of_total}%` : ""}</span>
                      <span className="flex items-center gap-2"><span className="font-semibold text-slate-800">{inr(c.cost)}</span><Delta pct={c.change_pct} /></span>
                    </div>
                  ))}
                </div>
                {ic.top_products.length > 0 && (
                  <div className="mt-3 border-t border-slate-100 pt-2">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Top products</p>
                    {ic.top_products.map((p) => (
                      <div key={p.name} className="flex items-center justify-between text-xs" data-testid={`mi-cons-prod-${p.name}`}>
                        <span className="text-slate-600 truncate">{p.name}</span>
                        <span className="font-semibold text-slate-800">{inr(p.cost)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Expense Intelligence */}
          <div className="rounded-2xl border border-slate-100 p-4" data-testid="mi-expenses">
            <div className="flex items-center gap-2 text-slate-700"><Receipt className="h-4 w-4" /><span className="text-sm font-bold">Operating Expenses</span></div>
            {ex?.has_comparison && ex.total > 0 && <div className="mt-1 flex items-center gap-1 text-xs text-slate-400">vs previous <Delta pct={ex.change_pct} /></div>}
            <ExpenseHeads range={{ start: istDay(data?.period?.from), end: istDay(data?.period?.to, 1) }} />
          </div>
        </div>
      )}

      {/* Cost-efficiency / attention insights */}
      {!isLoading && (data?.insights?.length > 0) && (
        <div className="mt-5 space-y-2" data-testid="mi-insights">
          {data.insights.map((ins, i) => {
            const t = TONE[ins.tone] || TONE.info;
            return (
              <div key={i} className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-xs ${t.c}`} data-testid={`mi-insight-${i}`}>
                <t.I className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{ins.text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
